// ============================================================================
// GsNotificationBell — cloche in-app transverse (micro-brique ntf.*)
// ============================================================================
// SPEC-FEEDBACK-TRANSVERSE §5 / BLITZ-FBK-FOUNDATION 2026-06-11. Badge compteur
// + liste 20 + marquer lu, POLLING modeste (AUCUN websocket).
//
// PROVIDER/CAPABILITY-AWARE (meme principe que l'item Langue du GsUserMenu) :
// si la route /api/notifications repond 404 (non montee dans cette app), 503
// (migration 116 en attente) ou 401/403, la cloche SE MASQUE — aucun controle
// mort dans les apps non branchees, aucun 500 surface a l'utilisateur.
//
// Pas de t() dans @gs/ui (pattern GsUserMenuLabels) : libelles via props,
// defauts FR. kinds -> libelles localises via labels.kindLabels.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Bell } from 'lucide-react';
import { api } from '@gs/api-client';

export interface GsNotificationItem {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  refType: string | null;
  refId: string | null;
  createdAt: string;
  read: boolean;
}

export interface GsNotificationBellLabels {
  /** aria-label du bouton (defaut « Notifications »). */
  bell?: string;
  empty?: string;
  markAllRead?: string;
  /** kind -> libelle localise (ex { FEEDBACK_CREATED: 'Nouveau commentaire' }). */
  kindLabels?: Record<string, string>;
}

export interface GsNotificationBellProps {
  /** Desactive le polling tant que l'app n'est pas prete (ex: !user). */
  enabled?: boolean;
  /** Endpoint (defaut /api/notifications — createNotificationsRoute @gs/api-shared). */
  endpoint?: string;
  /** Intervalle de polling ms (defaut 60 s — cloche, pas du temps reel). */
  pollMs?: number;
  /** Seuil « N+ » sur la pastille (defaut 9 ; 99 pour fidelite TopBar bureau PoC, F-COQ-74). */
  unreadBadgeCap?: number;
  /** Clic sur une notification (deep-link applicatif), apres marquage lu. */
  onNavigate?: (notification: GsNotificationItem) => void;
  labels?: GsNotificationBellLabels;
}

interface BellResponse {
  items: GsNotificationItem[];
  unreadCount: number;
}

function isCapabilityError(err: unknown): boolean {
  const status = (err as { status?: number } | null)?.status;
  return status === 404 || status === 503 || status === 401 || status === 403;
}

export function GsNotificationBell({
  enabled = true,
  endpoint = '/api/notifications',
  pollMs = 60_000,
  unreadBadgeCap = 9,
  onNavigate,
  labels,
}: GsNotificationBellProps) {
  const [capable, setCapable] = useState<boolean | null>(null); // null = pas encore sonde
  const [data, setData] = useState<BellResponse | null>(null);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // setState UNIQUEMENT dans les callbacks .then/.catch (reponse reseau = systeme
  // externe) — un setState synchrone dans le corps de l'effet est rejete par le
  // React Compiler (erreur lint preexistante :85, corrigee CHORE-NTF-BELL-LU).
  const refresh = useCallback(() => {
    api.get<BellResponse>(endpoint)
      .then((res) => {
        setData(res);
        setCapable(true);
      })
      .catch((err: unknown) => {
        if (isCapabilityError(err)) setCapable(false); // route absente / pre-116 -> cloche masquee
        // autre erreur (reseau transitoire) : on garde l'etat courant
      });
  }, [endpoint]);

  useEffect(() => {
    if (!enabled) return;
    refresh();
    const id = window.setInterval(refresh, pollMs);
    return () => window.clearInterval(id);
  }, [enabled, pollMs, refresh]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!enabled || capable !== true) return null;

  const unread = data?.unreadCount ?? 0;
  const items = data?.items ?? [];

  // Marquer lu (item / tout) — JAMAIS supprimer (doctrine NTF-LU-JAMAIS-SUPPRIME :
  // une notification est une trace d'audit ; l'UI n'offre aucune destruction).
  const markRead = async (id: string) => {
    try {
      await api.post(`${endpoint}/${id}/read`, {});
      refresh();
    } catch { /* best-effort */ }
  };
  const markAll = async () => {
    try {
      await api.post(`${endpoint}/read-all`, {});
      refresh();
    } catch { /* best-effort */ }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${labels?.bell ?? 'Notifications'}${unread > 0 ? ` (${unread})` : ''}`}
        className="touch-manipulation relative rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
      >
        <Bell size={15} aria-hidden />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-brand-500 px-0.5 text-chip font-bold text-white">
            {unread > unreadBadgeCap ? `${unreadBadgeCap}+` : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          aria-label={labels?.bell ?? 'Notifications'}
          className="absolute right-0 top-full z-50 mt-1 w-80 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900"
        >
          <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2 dark:border-slate-800">
            <span className="text-meta font-semibold text-slate-700 dark:text-slate-200">
              {labels?.bell ?? 'Notifications'}
            </span>
            {unread > 0 && (
              <button type="button" onClick={() => void markAll()}
                className="touch-manipulation text-chip font-medium text-slate-400 transition-colors hover:text-slate-600 dark:hover:text-slate-300">
                {labels?.markAllRead ?? 'Tout marquer lu'}
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <p className="px-3 py-5 text-center text-meta text-slate-400">
              {labels?.empty ?? 'Aucune nouvelle notification.'}
            </p>
          ) : (
            <ul className="max-h-80 overflow-y-auto">
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      void markRead(n.id);
                      setOpen(false);
                      onNavigate?.(n);
                    }}
                    className="touch-manipulation block w-full px-3 py-2 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    <span className="flex items-center gap-1.5">
                      {/* Non-lue = point brand + texte plein ; lue = grisee (crans
                          existants, aucun token nouveau — CHORE-NTF-BELL-LU P1). */}
                      {!n.read && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500" aria-hidden />}
                      <span className={`text-chip font-semibold uppercase tracking-wide ${n.read ? 'text-slate-300 dark:text-slate-600' : 'text-slate-400'}`}>
                        {labels?.kindLabels?.[n.kind] ?? n.kind}
                      </span>
                    </span>
                    <span className={`mt-0.5 block truncate text-meta ${n.read ? 'font-normal text-slate-400 dark:text-slate-500' : 'font-semibold text-slate-700 dark:text-slate-200'}`}>
                      {n.title}
                    </span>
                    {n.body && (
                      <span className={`block truncate text-chip ${n.read ? 'text-slate-300 dark:text-slate-600' : 'text-slate-400'}`}>{n.body}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
